import { Pause } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import type { BatchStatus } from '~/features/catalog-operations/types/catalog-operations.types';
import type { CostWaiting } from '~/features/cost-estimate/utils/cost-waiting';
import { cn } from '~/utils/cn';
import { formatDateTime, formatRelative } from '~/utils/date';

/** Status → semantic-token pair. `needs-attention` keeps its existing
 *  orange treatment until the design spec covers it explicitly. */
const STATUS_STYLE: Record<BatchStatus, { label: string; className: string }> = {
  'in-progress': { label: 'In Progress', className: 'bg-info-50 text-info' },
  delivered: { label: 'Delivered', className: 'bg-success-50 text-success' },
  failed: { label: 'Failed', className: 'bg-error-50 text-error' },
  'needs-attention': {
    label: 'Needs Attention',
    className: 'bg-warning-50 text-warning',
  },
  'awaiting-approval': {
    label: 'Review',
    className: 'bg-warning-50 text-warning',
  },
  // Delivering reuses the pipeline's "Delivering" stage orange (stage-active) so
  // the chip and the stage node read as the same state.
  delivering: { label: 'Delivering', className: 'bg-stage-active-bg text-stage-active' },
  // Rejected is terminal but NOT Failed — a neutral treatment keeps it visually
  // distinct from the error-red `failed` chip (no dedicated reject token yet).
  rejected: { label: 'Rejected', className: 'bg-muted text-muted-foreground' },
  running: { label: 'Running', className: 'bg-exp-status-running-bg text-exp-status-running' },
  completed: { label: 'Completed', className: 'bg-success-50 text-success' },
  cancelled: { label: 'Cancelled', className: 'bg-error-50 text-error' },
};

interface StatusChipProps {
  status: BatchStatus;
  className?: string;
  /** Overrides the default status label while keeping its color treatment —
   *  e.g. a fully-completed trial batch shows "Completed" on a `delivered` chip. */
  label?: string;
  /** Automation hook: overrides the default `status-chip` `data-testid` (e.g. per-row ids). */
  testId?: string;
  /** Run paused at the cost gate: replaces the running treatment with a warning-toned
   *  "Awaiting cost approval" pill (pause icon, paused-since tooltip). */
  costWaiting?: CostWaiting | null | undefined;
}

/** Text-only batch status pill. Width grows with the label per spec; padding
 *  + radius track the Figma geometry. */
export function StatusChip({ status, className, label, testId, costWaiting }: StatusChipProps) {
  const cfg = STATUS_STYLE[status];
  if (costWaiting)
    return (
      <CostWaitingChip
        waiting={costWaiting}
        className={className}
        testId={testId ?? 'status-chip'}
      />
    );
  return (
    <span
      data-testid={testId ?? 'status-chip'}
      className={cn(
        'inline-flex h-9 items-center justify-center rounded-lg px-3 py-1.5 text-sm font-medium',
        cfg.className,
        className,
      )}
    >
      {label ?? cfg.label}
    </span>
  );
}

/** The "Awaiting cost approval" variant — same geometry as the status pill, warning tone. */
function CostWaitingChip({
  waiting,
  className,
  testId,
}: {
  waiting: CostWaiting;
  className: string | undefined;
  testId: string;
}) {
  const { t } = useTranslation('common');
  const chip = (
    <span
      data-testid={testId}
      data-status="awaiting-cost-approval"
      className={cn(
        'bg-warning-50 text-warning inline-flex h-9 items-center justify-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium whitespace-nowrap',
        className,
      )}
      {...(waiting.waitingSince ? { tabIndex: 0 } : {})}
    >
      <Pause className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      {t('costGate.awaiting')}
    </span>
  );
  if (!waiting.waitingSince) return chip;
  return (
    <Tooltip delayDuration={200}>
      <TooltipTrigger asChild>{chip}</TooltipTrigger>
      <TooltipContent data-testid={`${testId}-tooltip`} className="max-w-xs text-xs">
        <p>{t('costGate.chipTooltip')}</p>
        <p>
          {t('costGate.paused')} {formatRelative(waiting.waitingSince)} (
          {formatDateTime(waiting.waitingSince)})
        </p>
      </TooltipContent>
    </Tooltip>
  );
}
